/**
 * dashboard/shared.tsx — presentational pieces shared by the three Manager Metrics Dashboard pages
 * (Overview · Tooling · Learning). Reuses the admin frame's ADMIN_STYLES + provisional posture and
 * adds a small set of metric primitives:
 *
 *   MetricCard      — one figure with a label and an optional note (the plain, real-number card).
 *   AsOfNote        — when the figures on this page were read (MD-NFR02).
 *   EstimateCard    — a figure explicitly labelled an ESTIMATE, always showing its inputs (MD-NFR06).
 *   PeopleCountCard — a count of people, or its absence: one component, so a suppressed figure can
 *                      never be rendered as a zero by mistake.
 *   RateCard        — a percentage shown WITH the two counts behind it (a bare percentage is the
 *                      classic way to mislead with a true number).
 *   AwaitingDataCard — the honest placeholder for a figure with no value to show (MD-F05): it states
 *                      WHY, in one of five reasons that each imply a different next action.
 *   SuppressedNote  — the cohort-suppression copy ("too few people to report", MD-NFR05).
 *
 * Every card is text/table-based (no colour-only charts) so it carries its own text equivalent
 * (MD-NFR07). Nothing here names or ranks a person (MD-NFR04/10).
 */
import type { ReactNode } from 'react';
import { shortWhen } from '../community/shared';
import type { FigureAbsence, PeopleFigure, RateFigure } from './api';

/**
 * The manager dashboard's stylesheet, in the teal HOUSE design system (design-system.css tokens),
 * to match the admin overlay panels. This used to be a thin add-on layered over the navy
 * `ADMIN_STYLES` (`--tb-*` tokens from App.tsx :root); it is now SELF-CONTAINED — it folds in the
 * few base rules the pages borrowed (`.tb-admin` frame, `.tb-provisional`, `.tb-field`/`.tb-submit`,
 * `.tb-list`/`.tb-item-*`, `.tb-muted`, `.tb-empty`) and paints every rule with the design tokens
 * (`--surface`, `--ink`, `--rule`, `--accent`, `--text`, `--text-inactive`, `--border-subtle`, the
 * `--radius-*`/`--space-*`/`--text-*` ramps). CLASS NAMES ARE UNCHANGED, so no page markup moves —
 * this is presentation-only. Muted text reads `var(--text-inactive)`, which the `.admin-surface`
 * wrapper on the layout root darkens to a legible 0.55 exactly as the admin panels do.
 */
export const DASHBOARD_STYLES = `
  .tb-admin { max-width: 64rem; font-weight: var(--weight-light); color: var(--text); }
  .tb-admin h1 { margin-top: 0; font-size: var(--text-xl); font-weight: var(--weight-light); }
  .tb-admin h2 { font-size: var(--text-lg); font-weight: var(--weight-medium); }
  .tb-admin h3 { font-size: var(--text-md); font-weight: var(--weight-medium); }
  .tb-admin .tb-muted { color: var(--text-inactive); }

  .tb-admin .tb-provisional {
    border: 1px solid var(--border-subtle);
    border-left: 4px solid var(--accent);
    background: var(--accent-subtle);
    color: var(--text);
    padding: 0.6rem 0.9rem; margin: 0 0 1.25rem;
    border-radius: var(--radius-sm); font-size: var(--text-sm);
  }

  .tb-dash-tabs { display: flex; flex-wrap: wrap; gap: 0.25rem; margin: 0 0 1rem; padding: 0;
    list-style: none; border-bottom: 1px solid var(--rule); }
  .tb-dash-tabs a {
    display: inline-block; padding: 0.5rem 0.9rem; text-decoration: none; color: var(--text);
    border: 1px solid transparent; border-bottom: none; border-radius: var(--radius-sm) var(--radius-sm) 0 0;
    margin-bottom: -1px; font-size: var(--text-sm);
  }
  .tb-dash-tabs a:hover { background: var(--accent-subtle); }
  .tb-dash-tabs a.active { border-color: var(--rule); background: var(--surface);
    color: var(--accent); font-weight: var(--weight-medium); }

  .tb-dash-filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.75rem;
    margin: 0 0 1.25rem; padding: 0.75rem 0.9rem; border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md); background: var(--surface); }
  .tb-admin label.tb-field { display: block; margin: 0; min-width: 12rem; font-size: var(--text-sm); }
  .tb-admin label.tb-field > span { display: block; font-weight: var(--weight-medium);
    margin-bottom: 0.25rem; color: var(--text); }
  .tb-admin .tb-field select {
    width: 100%; font: inherit; padding: 0.4rem 0.5rem;
    border: 1px solid var(--border-control); border-radius: var(--radius-sm);
    background: var(--surface); color: var(--text);
  }
  .tb-admin button.tb-submit {
    font: inherit; padding: 0.4rem 0.9rem;
    border: 1px solid var(--accent); border-radius: var(--radius-sm);
    background: var(--accent); color: var(--text-on-accent); cursor: pointer;
    font-weight: var(--weight-medium);
  }
  .tb-admin button.tb-submit:hover { background: var(--accent-strong); border-color: var(--accent-strong); }
  .tb-admin button.tb-submit:disabled { opacity: 0.5; cursor: not-allowed; }

  .tb-dash-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    gap: 0.75rem; margin: 0.75rem 0 1.5rem; }
  .tb-card { border: 1px solid var(--border-subtle); border-radius: var(--radius-md);
    padding: 0.9rem 1rem; background: var(--surface); }
  .tb-card .tb-card-label { font-size: var(--text-xs); color: var(--text-inactive);
    text-transform: uppercase; letter-spacing: 0.03em; margin: 0 0 0.35rem; }
  .tb-card .tb-card-figure { font-size: var(--text-xl); font-weight: var(--weight-medium); line-height: 1.1; color: var(--text); }
  .tb-card .tb-card-note { font-size: var(--text-xs); color: var(--text-inactive); margin: 0.4rem 0 0; }
  .tb-card.tb-card-awaiting { background: var(--accent-subtle); border-style: dashed; }
  .tb-card.tb-card-awaiting .tb-card-figure { font-size: var(--text-md); font-weight: var(--weight-medium); color: var(--text-inactive); }
  .tb-card.tb-card-estimate { border-left: 4px solid var(--accent); }
  .tb-estimate-badge { display: inline-block; font-size: var(--text-xs); font-weight: var(--weight-medium);
    text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-on-accent);
    background: var(--accent-subtle); border: 1px solid var(--accent); border-radius: var(--radius-sm);
    padding: 0.05rem 0.35rem; margin-left: 0.4rem; vertical-align: middle; }

  .tb-admin ul.tb-list { list-style: none; margin: 1rem 0; padding: 0; }
  .tb-admin ul.tb-list > li {
    border: 1px solid var(--border-subtle); border-radius: var(--radius-sm);
    padding: 0.75rem 1rem; margin: 0.6rem 0; background: var(--surface);
  }
  .tb-admin .tb-item-title { font-weight: var(--weight-medium); font-size: var(--text-md);
    color: var(--accent-link); text-decoration: none; }
  .tb-admin a.tb-item-title:hover { text-decoration: underline; }
  .tb-admin .tb-item-meta { color: var(--text-inactive); font-size: var(--text-sm); margin: 0.15rem 0 0.5rem; }

  .tb-dash-table { width: 100%; border-collapse: collapse; margin: 0.75rem 0 1.5rem; }
  .tb-dash-table th, .tb-dash-table td { text-align: left; padding: 0.45rem 0.6rem;
    border-bottom: 1px solid var(--border-subtle); font-size: var(--text-sm); }
  .tb-dash-table th { color: var(--text-inactive); font-size: var(--text-xs); text-transform: uppercase;
    letter-spacing: 0.03em; font-weight: var(--weight-medium); }
  .tb-dash-table td.tb-num { text-align: right; font-variant-numeric: tabular-nums; }

  .tb-filter-live { font-size: var(--text-sm); color: var(--text-inactive); margin: 0 0 1rem; }
  .tb-as-of { font-size: var(--text-sm); color: var(--text-inactive); margin: -0.75rem 0 1rem; }
  .tb-admin .tb-empty { color: var(--text-inactive); font-style: italic; margin: 1rem 0; }

  /* Charts (charts.tsx). Deliberately quiet: they sit ABOVE the tables that carry the exact figures,
     so they are the glance, not the record. The bar fill is the one saturated mark; tracks and rules
     stay achromatic so a suppressed "Too few" row (no fill) reads as absence, not as a small value. */
  .tb-chart { margin: 0.75rem 0 1.25rem; padding: 0; border: 0; }
  .tb-chart-rows { display: flex; flex-direction: column; gap: 0.4rem; }
  .tb-chart-row { display: grid; grid-template-columns: minmax(8rem, 14rem) 1fr auto;
    align-items: center; gap: 0.75rem; }
  .tb-chart-label { font-size: var(--text-sm); color: var(--text); overflow-wrap: anywhere; }
  .tb-chart-label a { color: var(--accent-link); text-decoration: none; }
  .tb-chart-label a:hover { text-decoration: underline; }
  .tb-chart-track { height: 0.85rem; background: var(--accent-subtle);
    border-radius: var(--radius-sm); overflow: hidden; }
  .tb-chart-fill { height: 100%; background: var(--accent); border-radius: var(--radius-sm); }
  .tb-chart-value { font-size: var(--text-sm); font-variant-numeric: tabular-nums;
    text-align: right; color: var(--text); min-width: 3.5rem; }
  .tb-chart-value-suppressed { color: var(--text-inactive); font-style: italic; }

  .tb-seg-track { display: flex; height: 1.1rem; width: 100%; background: var(--border-subtle);
    border-radius: var(--radius-sm); overflow: hidden; margin: 0.5rem 0; }
  .tb-seg-fill { height: 100%; }
  .tb-seg-legend { display: flex; flex-wrap: wrap; gap: 0.25rem 1rem; list-style: none;
    margin: 0.35rem 0 0; padding: 0; font-size: var(--text-sm); color: var(--text); }
  .tb-seg-legend li { display: flex; align-items: center; gap: 0.4rem; }
  .tb-seg-swatch { display: inline-block; width: 0.7rem; height: 0.7rem;
    border-radius: 2px; border: 1px solid var(--border-subtle); }
  .tb-chart figcaption { margin-top: 0.5rem; font-size: var(--text-sm); max-width: 68ch; }

  /* Line chart (charts.tsx LineChart) — the active-people series, one point per day across the period.
     Same quiet palette: one accent stroke, achromatic baseline; a suppressed day is a GAP in the line
     (drawn by rendering separate polyline runs), never a point on the axis. The SVG stretches to the
     column width; preserveAspectRatio:none lets it fill without a fixed aspect, and vector-effect keeps
     the stroke 2px however far it is scaled up. */
  .tb-chart-line { margin-top: 0.5rem; }
  .tb-line-svg { display: block; width: 100%; height: 160px; overflow: visible; }
  .tb-line-axis { stroke: var(--border-subtle); stroke-width: 1; vector-effect: non-scaling-stroke; }
  .tb-line-stroke { fill: none; stroke: var(--accent); stroke-width: 2;
    stroke-linejoin: round; stroke-linecap: round; vector-effect: non-scaling-stroke; }
  .tb-line-dot { fill: var(--accent); }
  .tb-line-xaxis { display: flex; justify-content: space-between; margin-top: 0.35rem;
    font-size: var(--text-sm); font-variant-numeric: tabular-nums; color: var(--text-inactive); }
  .tb-chart-line-empty { margin: 0.5rem 0; }

  /* ── Full-bleed charcoal header band ──────────────────────────────────────────────────────────
     The manager dashboard's charcoal sibling of the /me and /admin bands, carrying the same animated
     Topography ripple recoloured WHITE against the dark ground ("carte blanche"). Same full-bleed
     idiom every designed surface uses: on the page root, cancel .tb-main's own top + side padding so
     the band butts flush under the app header and can run edge-to-edge, own the shared 64px inset as
     horizontal padding so the band content and everything below share one left edge, and drop the
     .tb-admin 64rem clamp so the bleed is not trapped in a narrow centred column. */
  .tb-dash-page {
    --dash-inset: 64px;
    max-width: none;
    margin-top: -1.5rem; /* cancel .tb-main's top padding so the band is flush under the header */
    margin-inline: -1rem; /* cancel .tb-main's 1rem side padding; we own the inset via --dash-inset */
    padding-inline: var(--dash-inset);
  }
  /* Charcoal→black, matching the admin band so the two manager-facing headers read as one system.
     Negates the inset with a negative margin, then restores it as padding on the inner band so the
     title lines up with the content below. */
  .tb-dash-band-bleed {
    background: linear-gradient(105deg, #17191b 0%, #101214 55%, #0a0b0c 100%);
    color: #eef1f2;
    margin-inline: calc(-1 * var(--dash-inset));
    margin-bottom: 28px;
  }
  .tb-dash-band {
    display: flex;
    position: relative; /* positioning context + pointer host for the decorative ripple */
    align-items: center;
    gap: 28px;
    padding: 32px var(--dash-inset);
    min-height: 193px; /* matches the profile + admin bands so the three headers read as one system */
  }
  .tb-dash-band__cluster { position: relative; z-index: 1; flex: 1 1 auto; min-width: 0; }
  .tb-admin h1.tb-dash-band__title {
    margin: 0;
    font-family: var(--font-sans);
    font-size: var(--text-xl, 24px);
    font-weight: var(--weight-medium, 500);
    line-height: 1.2;
    letter-spacing: 0.01em;
    color: #ffffff;
  }
  .tb-dash-band__subtitle {
    margin: 8px 0 0;
    font-size: var(--text-md, 16px);
    font-weight: var(--weight-light, 300);
    color: #b9c0c4;
  }
  /* The ripple, spanning the whole band and painting BEHIND the cluster (which carries z-index:1).
     Mirrors .admin-band__topo: absolutely positioned, never a hit-target, masked so it clears the
     left where the title sits and flows out to the edges everywhere else. */
  .tb-dash-band__topo {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    mask-image: linear-gradient(
      to right,
      rgb(0 0 0 / 0.12) 0%,
      rgb(0 0 0 / 0.4) 20%,
      #000 48%,
      #000 88%,
      rgb(0 0 0 / 0.35) 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      rgb(0 0 0 / 0.12) 0%,
      rgb(0 0 0 / 0.4) 20%,
      #000 48%,
      #000 88%,
      rgb(0 0 0 / 0.35) 100%
    );
  }
  /* Below the stack point the band goes vertical; drop the ripple rather than let it sit under the
     text — same call as the profile + admin bands. */
  @media (max-width: 720px) {
    .tb-dash-band { flex-direction: column; align-items: flex-start; gap: 18px; }
    .tb-dash-band__topo { display: none; }
  }

  @media print {
    .tb-dash-band-bleed { background: none; color: inherit; }
    .tb-dash-band__topo { display: none; }
    .tb-dash-tabs, .tb-dash-filters button, .tb-provisional { display: none; }
    .tb-card, .tb-chart { break-inside: avoid; }
  }
`;

/**
 * Human copy for a window of days, always read from the SERVER's own `windowDays` and never from a
 * constant here (MD-F24: a figure whose period is not stated could be all-time, and "N people" without
 * a period is not a fact a manager can act on).
 *
 * THE FALLBACK USED TO LIE, which is the same defect as hardcoding "30 days" wearing a function. Any
 * value that was not 7 or 180 came back as "last 30 days", so the copy was true only for as long as the
 * route's whitelist stayed exactly {7, 30, 180}: add 90 to MD §8's periods, or label a figure that keeps
 * its own horizon — MD-F37's neglected content is measured over 90 days whatever the page filter says —
 * and the page confidently states a period the number was never measured over. Every window now names
 * its own length, so an unfamiliar one reads oddly at worst and never reads falsely.
 *
 * 180 keeps its bespoke wording, and it is the ONLY special case: it is what the filter control itself
 * offers ("Last 6 months"), so the page and the selector have to agree. Everything else is spelled in the
 * days the server actually measured, rather than converted into months here — 180 days is six months only
 * under the 30-day month `make_interval(days => 180)` already assumes, and repeating that arithmetic for
 * every future window would invent precision the figure does not have.
 */
export function periodLabel(windowDays: number): string {
  if (windowDays === 180) return 'last 6 months';
  return `last ${windowDays} days`;
}

/**
 * MD-NFR02 — WHEN these figures were read. All three handlers have always stamped an `asOf` at the
 * moment they composed the response and no page rendered it, so a printed or left-open dashboard
 * carried no date at all: the reader could not tell this morning's position from last Friday's.
 *
 * The sentence states only what is true today. Each page is read once, by its react-router loader, when
 * you navigate to it or apply a filter — nothing polls and nothing is cached, so the stamp stays
 * accurate until you reload. Choosing a refresh model (a cadence, a manual refresh control, a staleness
 * warning) is the Tier 2 half of MD-NFR02 and is deliberately not decided here; do not let this copy
 * grow into a promise about one.
 *
 * `<time dateTime>` carries the machine-readable ISO instant while the text shows the reader's own
 * locale and timezone, which is what `shortWhen` gives every other timestamp in the product.
 */
export function AsOfNote({ asOf }: { asOf: string }) {
  return (
    <p className="tb-as-of">
      Figures as at <time dateTime={asOf}>{shortWhen(asOf)}</time>, when this page was read. They do
      not change until you reload the page or apply a filter.
    </p>
  );
}

/** A plain figure card: a big number (or any node), a label, and an optional sub-note. */
export function MetricCard({
  label,
  figure,
  note,
}: {
  label: string;
  figure: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="tb-card">
      <p className="tb-card-label">{label}</p>
      <div className="tb-card-figure">{figure}</div>
      {note != null && <p className="tb-card-note">{note}</p>}
    </div>
  );
}

/**
 * A figure that is explicitly an ESTIMATE (MD-NFR06): it carries an "estimate" badge and always
 * lists the inputs that produced it, so no one mistakes it for a measured fact.
 */
export function EstimateCard({
  label,
  figure,
  inputs,
}: {
  label: string;
  figure: ReactNode;
  inputs: string;
}) {
  return (
    <div className="tb-card tb-card-estimate">
      <p className="tb-card-label">
        {label}
        <span className="tb-estimate-badge">estimate</span>
      </p>
      <div className="tb-card-figure">{figure}</div>
      <p className="tb-card-note">{inputs}</p>
    </div>
  );
}

/**
 * The copy for each honest-unavailable reason (MD-F05) — never a fabricated zero.
 *
 * Each sentence names a DIFFERENT next action, which is the point of having five reasons rather than one
 * shrug. Wait for a deploy; wait for time to pass; narrow the period; go and record a headcount; wait for
 * more people to be counted. A reader who cannot tell which of those applies will read every blank card
 * as "broken".
 */
function unavailableCopy(reason: FigureAbsence): string {
  switch (reason) {
    case 'headcount-not-recorded':
      return 'No workforce size has been recorded for this scope, so a share of the workforce cannot be calculated. A platform owner can record one under Admin › Headcount.';
    case 'tracking-too-recent':
      return 'Activity recording started more recently than this period reaches back, so a figure for the whole period would be wrong rather than approximate. It appears once enough time has passed.';
    case 'retention-window-too-short':
      return 'This measure compares one period with the one before it, and that is further back than activity history is kept (90 days). Choose a shorter period.';
    case 'too-few':
      return 'Too few people to report without identifying someone. This figure appears once enough distinct people are counted.';
    default:
      return 'Awaiting activity tracking. No activity has been recorded yet, so there is nothing to measure.';
  }
}

/** The short headline shown in place of the number. */
function unavailableHeadline(reason: FigureAbsence): string {
  switch (reason) {
    case 'headcount-not-recorded':
      return 'Headcount not recorded';
    case 'tracking-too-recent':
      return 'Not enough history yet';
    case 'retention-window-too-short':
      return 'Period too long';
    case 'too-few':
      return 'Too few people';
    default:
      return 'Awaiting activity tracking';
  }
}

/** The honest placeholder for a figure with no value to show (MD-F05). Renders no number. */
export function AwaitingDataCard({
  label,
  marker,
}: {
  label: string;
  marker: { available: false; reason: FigureAbsence };
}) {
  return (
    <div className="tb-card tb-card-awaiting">
      <p className="tb-card-label">{label}</p>
      <div className="tb-card-figure">{unavailableHeadline(marker.reason)}</div>
      <p className="tb-card-note">{unavailableCopy(marker.reason)}</p>
    </div>
  );
}

/**
 * One people count, or its absence. The two are one component so that a page cannot accidentally render a
 * suppressed figure as a zero — the only way to reach the number is through the available branch.
 */
export function PeopleCountCard({
  label,
  figure,
  note,
}: {
  label: string;
  figure: PeopleFigure;
  note?: ReactNode;
}) {
  if (!figure.available) return <AwaitingDataCard label={label} marker={figure} />;
  return <MetricCard label={label} figure={figure.count.toLocaleString()} note={note} />;
}

/**
 * One rate, shown as a percentage WITH the two counts behind it.
 *
 * The counts are not decoration. A percentage alone is the classic way to mislead with a true number
 * — "40% returned" reads very differently from "2 of 5" — and both halves have already
 * cleared the cohort floor by the time this renders, so printing them costs no privacy. `note` is appended
 * after them rather than replacing them.
 */
export function RateCard({
  label,
  figure,
  unit,
  note,
}: {
  label: string;
  figure: RateFigure;
  /** What the two counts ARE, e.g. "active of workforce". Shown beneath the percentage. */
  unit: (numerator: number, denominator: number) => string;
  note?: ReactNode;
}) {
  if (!figure.available) return <AwaitingDataCard label={label} marker={figure} />;
  const pct = `${(figure.rate * 100).toFixed(figure.rate < 0.1 ? 1 : 0)}%`;
  return (
    <MetricCard
      label={label}
      figure={pct}
      note={
        <>
          {unit(figure.numerator, figure.denominator)}
          {note != null && <> {note}</>}
        </>
      }
    />
  );
}

/** The standard cohort-suppression note (MD-NFR05). */
export function SuppressedNote({ children }: { children?: ReactNode }) {
  return (
    <p className="tb-empty">
      {children ??
        'Too few people to report without identifying someone. This figure appears once enough distinct people are counted.'}
    </p>
  );
}
